import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { startAuthentication } from '@simplewebauthn/browser';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getPasskeyErrorMessage } from '../utils/passkeyErrors';
import './Auth.css';

const SHOW_GOOGLE_SIGNIN = false; // set to true to show "Continue with Google"

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passkeyStep, setPasskeyStep] = useState<{ options: any; passkeyToken: string } | null>(null);
  const passkeyTriggered = useRef(false);
  const { login, completePasskeyLogin, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryReturnTo = new URLSearchParams(location.search).get('returnTo');
  const returnTo = (location.state as any)?.returnTo || queryReturnTo || '/dashboard';
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  useEffect(() => {
    if (user) {
      navigate(user.isAdmin ? '/admin' : returnTo);
    }
  }, [navigate, returnTo, user]);

  useEffect(() => {
    if (!passkeyStep || passkeyTriggered.current) return;
    passkeyTriggered.current = true;
    let cancelled = false;
    (async () => {
      try {
        const response = await startAuthentication({ optionsJSON: passkeyStep.options });
        if (cancelled) return;
        const loggedInUser = await completePasskeyLogin(passkeyStep.passkeyToken, response);
        navigate(loggedInUser.isAdmin ? '/admin' : returnTo);
      } catch (err: any) {
        if (cancelled) return;
        const msg = err?.response?.data?.error || getPasskeyErrorMessage(err, 'login');
        setError(msg);
        setPasskeyStep(null);
        passkeyTriggered.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [passkeyStep, completePasskeyLogin, navigate, returnTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResendMessage('');
    setResendError('');
    setVerificationRequired(false);
    setPasskeyStep(null);
    passkeyTriggered.current = false;
    setLoading(true);

    try {
      const result = await login(email, password);
      if ('user' in result) {
        navigate(result.user.isAdmin ? '/admin' : returnTo);
        return;
      }
      if ('requiresPasskey' in result) {
        setPasskeyStep({ options: result.options, passkeyToken: result.passkeyToken });
        setLoading(false);
        return;
      }
    } catch (err: any) {
      const response = err.response?.data;
      setError(response?.error || 'Login failed');
      setVerificationRequired(Boolean(response?.verificationRequired));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setResendError('Please enter your email address to resend verification.');
      return;
    }
    setResendError('');
    setResendMessage('');
    setResendLoading(true);
    try {
      const response = await api.post('/auth/resend-verification', { email });
      setResendMessage(response.data?.message || 'Verification email sent. Please check your inbox.');
    } catch (err: any) {
      setResendError(err.response?.data?.error || 'Failed to resend verification email.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Login to Club Kazi</h1>
        <p className="auth-subtitle">Membership-only grocery store</p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                title={showPassword ? 'Hide password' : 'Show password'}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            <div className="auth-link">
              <Link to="/forgot-password">Forgot password?</Link>
            </div>
          </div>
          {passkeyStep && (
            <div className="passkey-prompt" style={{ marginTop: 12, padding: 12, background: '#f0f9ff', borderRadius: 8 }}>
              {loading ? 'Opening passkey…' : 'Use your passkey to sign in (Face ID, Touch ID, or device PIN).'}
            </div>
          )}
          {error && <div className="error">{error}</div>}
          {verificationRequired && (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleResendVerification}
                disabled={resendLoading}
              >
                {resendLoading ? 'Sending...' : 'Resend verification email'}
              </button>
              {resendMessage && <div className="success">{resendMessage}</div>}
              {resendError && <div className="error">{resendError}</div>}
            </div>
          )}
          <button type="submit" className="btn btn-primary" disabled={loading || !!passkeyStep}>
            {passkeyStep ? 'Waiting for passkey…' : loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        {SHOW_GOOGLE_SIGNIN && (
          <button
            type="button"
            className="btn btn-secondary auth-social-btn"
            onClick={() => {
              window.location.href = `${apiBaseUrl}/auth/google?returnTo=${encodeURIComponent(returnTo)}`;
            }}
          >
            Continue with Google
          </button>
        )}
        <p className="auth-footer">
          Don't have an account? <Link to="/register">Register here</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;

